import { markdownToHtml } from '@littlebird/markdown-utils';
import DOMPurify from 'dompurify';

import { logError } from '@/lib/errorLogging';

const SAFE_URI_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

// Navigable blob: URLs can load HTML in the renderer; allow them only as media
const MEDIA_URI_ATTRS = new Map<string, readonly string[]>([
  ['img', ['src', 'srcset']],
  ['source', ['src', 'srcset']],
  ['video', ['src', 'poster']],
  ['audio', ['src']],
]);

// Relative URIs inherit a safe protocol from this base
const RELATIVE_URI_BASE = 'https://relative.invalid';

// Match browser URL normalization so control characters cannot hide a scheme
const isDangerousUri = (
  value: string,
  element: Element,
  attributeName: string
): boolean => {
  let url: URL;
  try {
    url = new URL(value, RELATIVE_URI_BASE);
  } catch {
    return true;
  }
  if (url.protocol === 'data:') {
    const mediaType = url.pathname.toLowerCase();
    return !mediaType.startsWith('image/') || mediaType.startsWith('image/svg');
  }
  if (url.protocol === 'blob:') {
    return !MEDIA_URI_ATTRS.get(element.localName)?.includes(attributeName);
  }
  return !SAFE_URI_PROTOCOLS.has(url.protocol);
};

const URI_ATTRS = new Set(['href', 'src', 'poster', 'formaction', 'action']);

// Admits blob: because DOMPurify checks URI attributes against this after the hook below,
// and the hook is what limits blob: to media
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

// A private instance, so the hook does not change how other code's DOMPurify behaves
const purifier = DOMPurify(window);

purifier.addHook('uponSanitizeAttribute', (element, attribute) => {
  const { attrName, attrValue } = attribute;
  if (URI_ATTRS.has(attrName)) {
    if (isDangerousUri(attrValue, element, attrName))
      attribute.keepAttr = false;
  } else if (attrName === 'srcset') {
    const sanitized = attrValue
      .split(',')
      .filter((entry) => !isDangerousUri(entry.trim(), element, attrName))
      .join(',');
    if (sanitized) attribute.attrValue = sanitized;
    else attribute.keepAttr = false;
  }
});

/**
 * Sanitize an untrusted HTML string for rendering in the app.
 *
 * Drops SVG and MathML, which are common sources of sanitizer bypasses and which no
 * caller needs. Also drops elements that act on the whole page (`meta`, `base`, `link`,
 * `style`) and forms, including the `form` attribute that attaches a control to another form.
 */
export function sanitizeHtml(html: string): string {
  // DOMPurify returns its input untouched when it cannot run, so render nothing and report it
  if (!purifier.isSupported) {
    logError(
      new Error('DOMPurify is not supported here'),
      'Refused to render HTML without a sanitizer',
      { context: 'sanitizeHtml' }
    );
    return '';
  }
  return purifier.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'form'],
    // A popover draws above the whole window, outside the field's `contain: paint`
    FORBID_ATTR: ['form', 'popover', 'popovertarget', 'popovertargetaction'],
    ALLOWED_URI_REGEXP,
  });
}

const DOUBLE_NEWLINE_ELEMENTS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'PRE',
]);

function getCellText(cell: Element): string {
  return Array.from(cell.childNodes)
    .map((n) => htmlNodeToPlainText(n))
    .join('')
    .replace(/\n+/g, ' ')
    .trim();
}

function directChildren(el: Element, tag: string): Element[] {
  return Array.from(el.children).filter((c) => c.tagName === tag);
}

function formatTable(table: Element) {
  const sections = [
    ...directChildren(table, 'THEAD'),
    ...directChildren(table, 'TBODY'),
  ];
  const trs = sections.length
    ? sections.flatMap((s) => directChildren(s, 'TR'))
    : directChildren(table, 'TR');

  const rows: string[][] = [];
  for (const tr of trs) {
    rows.push(
      Array.from(tr.querySelectorAll(':scope > th, :scope > td')).map((cell) =>
        getCellText(cell)
      )
    );
  }
  if (!rows.length) return '';

  const thead = table.querySelector('thead');
  const hasHeader = thead !== null && thead.querySelector('th') !== null;
  const colCount = Math.max(...rows.map((r) => r.length));
  const colWidths = Array.from({ length: colCount }, (_, i) =>
    Math.max(...rows.map((r) => (r[i] ?? '').length))
  );

  const formatRow = (row: string[]) =>
    '| ' + row.map((cell, i) => cell.padEnd(colWidths[i])).join(' | ') + ' |';

  if (!hasHeader) return rows.map(formatRow).join('\n');

  const separator =
    '| ' + colWidths.map((w) => '-'.repeat(w)).join(' | ') + ' |';

  const [header, ...body] = rows;
  return [formatRow(header), separator, ...body.map(formatRow)].join('\n');
}

const LIST_TAGS = new Set(['UL', 'OL']);

function joinChildren(nodes: NodeListOf<ChildNode>, depth: number): string {
  const parts: string[] = [];
  const nodeArr = Array.from(nodes);
  for (let i = 0; i < nodeArr.length; i++) {
    let part = htmlNodeToPlainText(nodeArr[i], depth);
    if (
      part.endsWith('\n\n') &&
      nodeArr[i + 1] &&
      LIST_TAGS.has((nodeArr[i + 1] as Element).tagName)
    ) {
      part = part.slice(0, -1);
    }
    parts.push(part);
  }
  return parts.join('');
}

function htmlNodeToPlainText(node: Node, depth = 0): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';

  const el = node as Element;
  const tag = el.tagName;

  if (tag === 'TABLE') return formatTable(el) + '\n\n';
  if (tag === 'BR') return '\n';
  if (tag === 'INPUT') return '';

  if (tag === 'UL' || tag === 'OL') {
    return Array.from(el.childNodes)
      .filter((n) => n.nodeType !== Node.TEXT_NODE || n.textContent?.trim())
      .map((n) => htmlNodeToPlainText(n, depth + 1))
      .join('');
  }

  const children = joinChildren(el.childNodes, depth);

  if (tag === 'A') {
    const href = el.getAttribute('href') ?? '';
    if (!href || children === href) return children;
    return `${children} (${href})`;
  }

  if (tag === 'LI') {
    const indent = '  '.repeat(Math.max(0, depth - 1));
    const textParts: string[] = [];
    const nestedLists: string[] = [];
    for (const child of Array.from(el.childNodes)) {
      const childEl = child as Element;
      if (childEl.tagName === 'UL' || childEl.tagName === 'OL') {
        nestedLists.push(htmlNodeToPlainText(child, depth));
      } else {
        textParts.push(htmlNodeToPlainText(child, depth));
      }
    }
    const rawText = textParts.join('').trim();
    const nested = nestedLists.join('');
    const parent = el.parentElement?.tagName;
    const isOrdered = parent === 'OL';

    let prefix: string;
    if (isOrdered) {
      const ol = el.parentElement!;
      const siblings = Array.from(ol.children);
      const position = siblings.indexOf(el);
      const reversed = ol.hasAttribute('reversed');
      const start = Number(
        ol.getAttribute('start') ?? (reversed ? siblings.length : 1)
      );

      let index: number;
      const liValue = el.getAttribute('value');
      if (liValue !== null) {
        index = Number(liValue);
      } else {
        // Find the effective start by looking back for a preceding li[value]
        let base = start;
        let basePos = 0;
        for (let i = position - 1; i >= 0; i--) {
          const prev = siblings[i].getAttribute('value');
          if (prev !== null) {
            base = Number(prev);
            basePos = i;
            break;
          }
        }
        const offset = position - basePos;
        index = reversed ? base - offset : base + offset;
      }
      prefix = `${index}. `;
    } else {
      prefix = '- ';
    }

    const hangingIndent = indent + ' '.repeat(prefix.length);
    const lines = rawText.split('\n');
    const text = lines
      .map((line, i) => (i === 0 ? line : `${hangingIndent}${line}`))
      .join('\n');

    return `${indent}${prefix}${text}\n${nested}`;
  }

  if (DOUBLE_NEWLINE_ELEMENTS.has(tag)) return `${children}\n\n`;

  return children;
}

export function htmlToPlainText(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return htmlNodeToPlainText(doc.body)
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
}

/** Sanitized HTML and matching plain text for markdown, escape backslashes resolved. */
export function renderMarkdown(markdown: string) {
  const html = sanitizeHtml(markdownToHtml(markdown));
  return { html, text: htmlToPlainText(html) };
}
